import { Image, Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../component/menu/Header";
import SideMenu from "../../../component/menu/SideMenu";
import { constants } from "../../../constant/constants";
import FormHeaderComponent from "../../../component/FormHeaderComponent";
import PurchaseItemInputs from "../../../component/inventory/purchase-item/PurchaseItemInputs";
import ApiService from "../../../utils/ApiService";
import useUserStore from "../../../stores/store";
import { prepareDropdown } from "../../../services/inventory/inventory.service";
import { validatePurchaseItems } from "../../../services/inventory/purchase-items.service";
import PurchaseItemSkeleton from "../../../component/inventory/purchase-item/PurchaseItemInputSkeleton";
import CustomLoader from "../../../component/menu/CustomLoader";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useSocket } from "../../../hooks/useSocket";

export default function PurchaseItemAdd() {
  const { restaurantId, user, showNotification, setNetworkCall } = useUserStore();
  const navigate = useNavigate();
  const [purchaseItems, setPurchaseItems] = useState([
    {
      uuid: crypto.randomUUID(),
      ingredientId: "",
      quantity: "",
      unitPrice: "",
      carriageInwards: "",
      purchaseDate: "",
      supplierId: "",
      insertStatus: "",
      insertError: "",
    },
  ]);
  const [ingredientItems, setIngredientItems] = useState();
  const [suppliersDropdown, setSupplierDropdown] = useState();
  const [ingredientLoading, setIngredientLoading] = useState(true);
  const [supplierLoading, setSupplierLoading] = useState(true);
  const [hasFormSubmitted, setHasFormSubmitted] = useState(false);
  const [errMsg, setErrMs] = useState({msg: '', isAllFailed: false});  
  
  const isRetry = useRef(false);
  const purchaseItemsRef = useRef([]);

  const socket = useSocket(`${process.env.REACT_APP_IMAGE_BASE_URL}`);

  function onHandleAddPurchaseItemInput() {
    setPurchaseItems((prev) => [
      ...prev,
      {
        uuid: crypto.randomUUID(),
        ingredientId: "",
        quantity: "",
        unitPrice: "",
        carriageInwards: "",
        purchaseDate: "",
        supplierId: "",
        insertStatus: "",
        insertError: "",
      },
    ]);
  }

  useEffect(() => {
    getIngredientItems();
    getSuppliersDropdown();
  }, []);

  async function getSuppliersDropdown() {
    // if (!Boolean(ingredientId)) return;
    const params = {
      restaurantId: restaurantId,
      // ingredientId: ingredientId,
    };
    try {
      const res = await ApiService.GET_INVENTORY_SUPPLIER_DROPDOWN(params, user.token.token);
      if (res.code !== 200) {
        setIngredientLoading(false);
        showNotification(res.data.message);
        return;
      }
      const { payload } = res;
      const { records } = payload;
      setSupplierDropdown(prepareDropdown(records, "name", "id"));
      setIngredientLoading(false);
    } catch (err) {
      setIngredientLoading(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  async function getIngredientItems() {
    const params = {
      restaurantId: restaurantId,
    };
    try {
      const res = await ApiService.GET_INVENTORY_INGREDIENT_DROPDOWN(params, user.token.token);
      if (res.code !== 200) {
        setIngredientLoading(false);
        showNotification(res.data.message);
        return;
      }
      const { payload } = res;
      const { records } = payload;
      setIngredientItems(records);
      setIngredientLoading(false);
    } catch (err) {
      showNotification(err.response.data.message);
      setIngredientLoading(false);
      return;
    }
  }

  function onHandleRemovePurchaseItemInput(uuid) {
    setPurchaseItems((prev) => prev.filter((item) => item.uuid !== uuid));
  }

  async function createPurchaseItem(purchaseItems) {
    setHasFormSubmitted(true);
    setNetworkCall(true);
    const payload = {
      restaurantId: restaurantId,
      purchaseItems: purchaseItems,
    };

    try {
      const res = await ApiService.INVENTORY_PURCHASE_ITEMS_CREATE(payload, user.token.token);
      if (res.code !== 200) {
        setNetworkCall(false);
        showNotification(res.data.message);
        return;
      }
      setNetworkCall(false);
      // showNotification("Purchase items create successfully.", false);
      // return navigate("/inventory/purchase-item/reports");
    } catch (err) {
      setNetworkCall(false);
      // showNotification(err.response.data.message);
      return;
    }
  }

  function onHandleCancel() {
    navigate("/inventory/purchase-item/reports");
  }

  function onHandleSave(retryItem = null) {
    const targetItems = retryItem ? [retryItem] : purchaseItems;

    const purchaseItemFiltered = targetItems.filter((el) => {
      // Check if at least one field has a value
      const hasAnyValue =
        el.ingredientId !== "" ||
        el.quantity !== "" ||
        el.unitPrice !== "" ||
        el.carriageInwards !== "" ||
        (el.purchaseDate !== "" && el.purchaseDate !== null) ||
        el.supplierId !== "";

      // Check if all fields are empty
      const allEmpty =
        el.ingredientId === "" &&
        el.quantity === "" &&
        el.unitPrice === "" &&
        el.carriageInwards === "" &&
        (el.purchaseDate === "" || el.purchaseDate === null) &&
        el.supplierId === "";

      return hasAnyValue && !allEmpty;
    });

    if (!retryItem) {
      purchaseItemsRef.current = purchaseItems;
    }

    if (validatePurchaseItems(purchaseItemFiltered)) {
      isRetry.current = retryItem ? true : false;
      createPurchaseItem(purchaseItemFiltered);
    }
  }

  const onHandleRetryInsert = (uuid) => {
    const failedItem = purchaseItems.find((item) => item.uuid === uuid);

    onHandleSave(failedItem);
  };

  const checkOverallStatus = (socketData) => {
    const { uuid, status, message } = socketData;
    purchaseItemsRef.current = purchaseItemsRef.current.map((item) =>
      item.uuid === uuid ? { ...item, insertStatus: status, insertError: message } : item
    );

    let total = purchaseItemsRef.current.length;
    let pendingCount = 0;
    let failedCount = 0;
    let successCount = 0;

    purchaseItemsRef.current.forEach(item => {
      if (item.insertStatus === '') {
        pendingCount += 1;
      } else if (item.insertStatus === 'Success') {
        successCount += 1;
      } else if (item.insertError) {
        failedCount += 1;
      }
    })

    if (isRetry.current && status === 'Success') {
      const msg = 'Item saved successfully';
      setErrMs({msg, isAllFailed: false});      
      showNotification(msg, false);
    } else if (isRetry.current && status !== 'Success') {
      const msg = 'Failed to save item.';
      setErrMs({msg, isAllFailed: true});      
      showNotification(msg, true);
    } else if (pendingCount === 0 && successCount === total) {
      const msg = 'All items saved successfully';
      setErrMs({msg, isAllFailed: false});      
      showNotification(msg, false);
    } else if (pendingCount === 0 && failedCount === total) {
      const msg = 'Failed to save items.';
      setErrMs({msg, isAllFailed: true});     
      showNotification(msg);
    } else if (pendingCount === 0) {
      const msg =  `${successCount} items saved successfully and ${failedCount} items failed.`;
      setErrMs({msg, isAllFailed: false});    
      showNotification(msg, false);
    }

    isRetry.current = false;

    if (failedCount === 0 && successCount === total) {
      navigate("/inventory/purchase-item/reports");
    }
  };

  /** Socket communication */
  useEffect(() => {
    if (!socket || !restaurantId) {
      return;
    }

    // New Order Placed
    socket.on(`${restaurantId}-insert-ingredient-purchase`, (data) => {
      console.log(`Event ${restaurantId}-insert-ingredient-purchase received in order details---data:`, {
        data,
      });

      const { uuid, status, message } = data;

      setPurchaseItems((prevItems) =>
        prevItems.map((item) => (item.uuid === uuid ? { ...item, insertStatus: status, insertError: message } : item))
      );

      checkOverallStatus(data);
    });

    // Cleanup
    return () => {
      socket.off(`${restaurantId}-insert-ingredient-purchase`);
    };
  }, [socket, restaurantId]);

  return (
    <div className="flex flex-col">
      <CustomLoader />
      <Header title={`McDonalds Food Chains`} menu={constants.MENU.INVENTORY} currentMenuKey={"T_7_S_7_L_2"} />
      <div className="flex flex-row bg-prewhite">
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.INVENTORY} />
        <div className="p-[24px] w-full mt-[80px] ml-[72px]">
          <div className="flex flex-col w-full">
            <FormHeaderComponent title={"Purchase item"} listRoute="/inventory/purchase-item/reports" />
          </div>

          {errMsg.msg && 
            <div className={`pl-[37px] bg-gray-100 rounded-md p-3 ${errMsg.isAllFailed ? 'text-red-600' : 'text-green'}`}>{errMsg.msg}</div>
          }

          <div className="w-full h-auto min-h-[50vh] bg-white rounded-[12px] pl-[37px] pt-[30px]">

            {/* item header */}
            <div className="flex items-center space-x-3 font-p_medium text-xs leading-[13px] text-black mb-[10px] mr-[37.67px]">
              <p className="w-[185px]">
                Item name <span className="text-red-500">*</span>
              </p>
              <p className="w-[82px]">
                Item Qty <span className="text-red-500">*</span>
              </p>
              <p className="w-[90px]">Item Unit</p>
              <p className="w-[100px]">
                Unit price <span className="text-red-500">*</span>
              </p>
              <p className="w-[87px]">Total Amount</p>
              <p className="w-[120px]">Carriage Inwards</p>
              <p className="w-[126px]">
                Purchase Date <span className="text-red-500">*</span>
              </p>
              <p className="w-[152px]">Supplier</p>
            </div>
            {purchaseItems.map((ing, idx) => (
              <div className="w-full flex items-center gap-x-[15px] mb-3" key={ing.uuid}>
                {ingredientLoading && supplierLoading ? (
                  <PurchaseItemSkeleton />
                ) : (
                  Boolean(ingredientItems) &&
                  ingredientItems.length > 0 && (
                    <div className="relative">
                      <PurchaseItemInputs
                        records={{ ingId: ing.uuid, purchaseItems, setPurchaseItems }}
                        dropdowns={{ ingredientItems: ingredientItems, suppliersDropdown: suppliersDropdown }}
                      />
                      {ing.insertStatus === "Success" && (
                        <div className="absolute inset-0 bg-transparent z-50 cursor-not-allowed" />
                      )}
                    </div>
                  )
                )}

                {!(ingredientLoading && supplierLoading) && ing.insertStatus === "" && (
                  <>
                    {purchaseItems.length > 1 && (
                      <button className="" onClick={() => onHandleRemovePurchaseItemInput(ing.uuid)}>
                        <Image src="/images/icons/remove_one.svg" preview={false} />
                      </button>
                    )}
                    {idx === purchaseItems.length - 1 && (
                      <button className="" onClick={onHandleAddPurchaseItemInput}>
                        <Image src="/images/icons/add_one.svg" preview={false} />
                      </button>
                    )}
                  </>
                )}

                {ing.insertStatus === "Success" && (
                  <button className="">
                    <Image width={36} height={36} className="bg-red" src="/images/tickicon.svg" preview={false} />
                  </button>
                )}

                {ing.insertStatus === "Failed" && (
                  <>
                    <button className="">
                      <Image
                        width={36}
                        height={36}
                        className="bg-red"
                        src="/images/icons/closeicon2.svg"
                        preview={false}
                      />
                    </button>
                    <button className="" onClick={() => onHandleRetryInsert(ing.uuid)}>
                      <Image src="/images/refresh-button.svg" preview={false} />
                    </button>
                    {ing.insertError && (
                      <button className="">
                        <Tooltip title={ing.insertError}>
                          <InfoCircleOutlined className="text-red-600 cursor-pointer" />
                        </Tooltip>
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}

            <div className="flex items-center space-x-3 pt-[76px] pb-5">
              <button
                onClick={onHandleCancel}
                className="font-bold w-[100px] h-[42px] bg-[#FCBD10] text-black rounded-[5px] text-base leading-[16px]"
              >
                {hasFormSubmitted ? "Back" : "Cancel"}
              </button>
              {!hasFormSubmitted && (
                <button
                  onClick={() => onHandleSave()}
                  className="font-bold w-[100px] h-[42px] bg-black text-white rounded-[5px] text-base leading-[16px]"
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
